import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Menu, MenuItem } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { findAppNameFromId } from 'helpers/index';
import { useAppSelector } from 'reduxState/hooks';
import { Application } from 'reduxState/store/application/types';
import { RestrictedUserRoles } from 'reduxState/store/user/constants';
import { selectUserRestrictedRole } from 'reduxState/store/user/selectors';
import './ApplicationDropdownMenu.scss';
import { trimAppName } from 'components/AppGroupOverview/helpers';

interface ApplicationDropdownMenuProps {
  appId?: string;
  applications: Application[];
  adminAppId: string;
  appGroupName?: string;
}

const ApplicationDropdownMenu: FC<ApplicationDropdownMenuProps> = ({
  appId,
  applications,
  adminAppId,
  appGroupName,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeApplicationName, setActiveApplicationName] = useState<null | string>(null);
  const restrictedUser = useAppSelector(selectUserRestrictedRole);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (pathname.includes('app-group')) {
      setActiveApplicationName('App Group Overview');
    } else {
      setActiveApplicationName(findAppNameFromId(applications, appId || ''));
    }
  }, [applications, appId, pathname]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (application: Application) => {
    setAnchorEl(null);
    const defaultPage = restrictedUser === RestrictedUserRoles.OPERATIONS ? 'commission-history' : 'summary';
    navigate(`/${adminAppId}/app/${application.id}/${defaultPage}`);
  };

  return (
    <div className="application-list-container">
      <Button
        classes={{ root: 'active-application-button truncate py-0' }}
        aria-controls="application-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        {appGroupName}
        {anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </Button>
      <Menu
        id="application-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PopoverClasses={{ paper: 'application-menu-popup' }}
        classes={{ list: 'application-list-popup' }}
        disableScrollLock
      >
        {applications.map((application: Application) => {
          return (
            <MenuItem
              key={application.name}
              classes={{ root: `list-item ${application.name === activeApplicationName ? 'active' : 'inactive'}` }}
              onClick={() => handleSelect(application)}
            >
              {trimAppName(application.name, appGroupName || '')}
            </MenuItem>
          );
        })}
        {applications.length > 1 && (
          <MenuItem
            classes={{
              root: `list-item view-all ${activeApplicationName === 'App Group Overview' ? 'active' : 'inactive'}`,
            }}
            onClick={() => {
              handleClose();
              setActiveApplicationName('App Group Overview');
              navigate(`/${adminAppId}`);
            }}
          >
            App Group Overview
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default ApplicationDropdownMenu;
